import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { Box, Typography, Paper, CircularProgress, IconButton, Tooltip, Chip } from '@mui/material';
import { Person, Assistant, ContentCopy, Check, PlayArrow, Pause } from '@mui/icons-material';
import { useConversation } from '../contexts/ConversationContext';
import TypingIndicator from './TypingIndicator';
import AudioWaveform from './AudioWaveform';
import FormattedText from './FormattedText';
import CitationFlyout from './CitationFlyout';
import FeedbackButtons from './FeedbackButtons';
import ImageCarousel from './ImageCarousel';
import ImageThumbnailChip from './ImageThumbnailChip';
import { ProductTiles } from './ProductTiles';
import ProductQuickViewModal from './ProductQuickViewModal';
import { getTextDirectionStyles, getMessageFlexDirection } from '../utils/textDirection';
import { Citation } from '../types/citation';
import { Message, ImageMetadata, ProductActionSourceProduct, ProductActionType } from '../types/conversation';
import { useAppConfig } from '../contexts/ConfigContext';

// Inline Audio Player Component
interface InlineAudioPlayerProps {
  messageContent: string;
  messageId: string;
  isGenerating: boolean;
  audioData?: string;
  onTTSRequest: (content: string, messageId: string) => void;
}

const InlineAudioPlayer: React.FC<InlineAudioPlayerProps> = ({
  messageContent,
  messageId,
  isGenerating,
  audioData,
  onTTSRequest
}) => {  const { config } = useAppConfig();
  const { setCurrentlyPlayingMessage, currentlyPlayingMessageId, messages } = useConversation();const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [autoPlayAttempted, setAutoPlayAttempted] = useState(false);  const audioRef = useRef<HTMLAudioElement>(null);
  // Check if any other message is generating TTS
  const isAnyOtherMessageGeneratingTTS = messages.some((msg: Message) => 
    msg.id !== messageId && msg.isGeneratingTTS
  );

  // Clean content for TTS (remove citations)
  const cleanContentForTTS = (content: string): string => {
    const citationPattern = /\[\s*\{[^}]*(?:'url'|"url")[^}]*\}(?:\s*,\s*\{[^}]*(?:'url'|"url")[^}]*\})*\s*\]/g;
    return content.replace(citationPattern, '').trim();
  };

  // Format time for display
  const formatTime = (time: number) => {
    if (!time || isNaN(time) || !isFinite(time) || time < 0) {
      return '0:00';
    }
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Toggle audio playback
  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !audioData) {
      // If no audio data, request TTS generation
      if (!audioData && !isGenerating) {
        const cleanContent = cleanContentForTTS(messageContent);
        onTTSRequest(cleanContent, messageId);
      }
      return;
    }    if (isPlaying) {
      // Stop current audio
      audio.pause();
      setIsPlaying(false);
      setCurrentlyPlayingMessage(null, false); // Clear currently playing message
    } else {
      // Check if another message is currently playing and stop it
      if (currentlyPlayingMessageId && currentlyPlayingMessageId !== messageId) {
        // Another message is playing, stop it by setting the global state
        setCurrentlyPlayingMessage(null, false);
      }
      
      setIsLoading(true);
      audio.play().then(() => {
        setIsPlaying(true);
        setIsLoading(false);
        setError(null);
        setCurrentlyPlayingMessage(messageId, true); // Set this message as currently playing
      }).catch((error) => {
        console.error('Audio playback failed:', error);
        setIsPlaying(false);
        setIsLoading(false);
        setError(`Playbook failed: ${error.message}`);
        setCurrentlyPlayingMessage(null, false); // Ensure no message is marked as playing on error
      });
    }
  }, [isPlaying, audioData, messageContent, messageId, isGenerating, onTTSRequest, setCurrentlyPlayingMessage, currentlyPlayingMessageId]);

  // Set up audio event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioData) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleDurationChange = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
      }
    };    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      audio.currentTime = 0;
      setCurrentlyPlayingMessage(null, false); // Clear currently playing message when audio ends
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setCurrentlyPlayingMessage(messageId, true); // Set this message as currently playing
    };
    
    const handlePause = () => {
      setIsPlaying(false);
      setCurrentlyPlayingMessage(null, false); // Clear currently playing message when paused
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('loadedmetadata', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    // Initial duration sync
    if (audio.readyState >= 1 && isFinite(audio.duration) && audio.duration > 0) {
      setDuration(audio.duration);
    }

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('loadedmetadata', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [audioData, setCurrentlyPlayingMessage, messageId]);

  // Autoplay when audio data becomes available
  useEffect(() => {
    if (audioData && audioRef.current && !autoPlayAttempted) {
      const audio = audioRef.current;
      setAutoPlayAttempted(true);
      
      const tryAutoPlay = async () => {
        try {
          // Small delay to ensure audio is ready
          await new Promise(resolve => setTimeout(resolve, 100));          if (audio.readyState >= 1) {
            await audio.play();
            setIsPlaying(true);
            setCurrentlyPlayingMessage(messageId, true); // Set this message as currently playing
            console.log('Audio autoplay successful');
          } else {
            // Wait for metadata to load
            const handleCanPlay = async () => {
              try {
                await audio.play();
                setIsPlaying(true);
                setCurrentlyPlayingMessage(messageId, true); // Set this message as currently playing
                console.log('Audio autoplay successful after canplay');
              } catch (error) {
                console.log('Autoplay failed:', error);
              }
              audio.removeEventListener('canplay', handleCanPlay);
            };
            audio.addEventListener('canplay', handleCanPlay);
          }
        } catch (error) {
          console.log('Autoplay failed (user interaction may be required):', error);
        }
      };

      tryAutoPlay();
    }
  }, [audioData, autoPlayAttempted, setCurrentlyPlayingMessage, messageId]);
  // Cleanup when component unmounts - ensure global audio state is reset
  useEffect(() => {
    return () => {
      // If this component was playing audio when it unmounts, reset the global state
      if (isPlaying && currentlyPlayingMessageId === messageId) {
        setCurrentlyPlayingMessage(null, false);
      }
    };
  }, [isPlaying, setCurrentlyPlayingMessage, currentlyPlayingMessageId, messageId]);

  // Listen for changes in currently playing message to stop this audio if another starts
  useEffect(() => {
    if (currentlyPlayingMessageId && currentlyPlayingMessageId !== messageId && isPlaying) {
      // Another message started playing, stop this one
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        audio.pause();
        setIsPlaying(false);
        setCurrentTime(0);
        audio.currentTime = 0;
      }
    }
  }, [currentlyPlayingMessageId, messageId, isPlaying]);

  // Handle progress bar click for seeking
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration || !isFinite(duration)) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newTime = percent * duration;

    if (isFinite(newTime)) {
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: { xs: 0.5, sm: 1 },
      minWidth: 0, // Allow shrinking
      overflow: 'hidden', // Prevent overflow
      maxWidth: '100%'
    }}>      {/* Audio element */}
      {audioData && (
        <audio 
          ref={audioRef} 
          src={`data:audio/mp3;base64,${audioData}`} 
          preload="metadata"
          key={audioData} // Force re-render when audioData changes
          onLoadedMetadata={() => {
            console.log('Audio metadata loaded');
            const audio = audioRef.current;
            if (audio && isFinite(audio.duration)) {
              setDuration(audio.duration);
            }
          }}
          onError={(e) => {
            console.error('Audio loading error:', e);
            setError('Failed to load audio');
            setIsLoading(false);
            setIsPlaying(false);
          }}
        />
      )}      {/* Play/Pause Button */}      <Tooltip title={
        !audioData ? (config?.ui_tooltips?.generate_audio_tooltip || "Generate Audio") : 
        isLoading ? "Loading..." :
        isAnyOtherMessageGeneratingTTS ? "Another message is generating audio..." :
        currentlyPlayingMessageId && currentlyPlayingMessageId !== messageId ? "Another audio is playing" :
        isPlaying ? (config?.ui_tooltips?.pause_audio_tooltip || "Pause") : (config?.ui_tooltips?.play_button_tooltip || "Play")
      }>
        <IconButton
          onClick={togglePlayback}
          size="small"
          disabled={
            isLoading || 
            isAnyOtherMessageGeneratingTTS ||
            (currentlyPlayingMessageId !== null && currentlyPlayingMessageId !== messageId && !isPlaying)
          }
          sx={{
            bgcolor: config?.ui_theme?.play_button_background || 'rgba(25, 118, 210, 0.08)',
            color: config?.ui_theme?.play_button_icon || '#1976d2',
            border: `1px solid ${config?.ui_theme?.play_button_border || 'rgba(25, 118, 210, 0.23)'}`,
            width: { xs: 28, sm: 32 }, // Smaller on mobile
            height: { xs: 28, sm: 32 }, // Smaller on mobile
            flexShrink: 0, // Prevent shrinking
            '&:hover': {
              bgcolor: config?.ui_theme?.play_button_background ? 
                `${config.ui_theme.play_button_background}aa` : 'rgba(25, 118, 210, 0.12)',
            }
          }}
        >
          {isLoading ? (
            <CircularProgress size={14} />
          ) : isPlaying ? (
            <Pause sx={{ fontSize: { xs: 14, sm: 16 } }} />
          ) : (
            <PlayArrow sx={{ fontSize: { xs: 14, sm: 16 } }} />
          )}
        </IconButton>
      </Tooltip>      {/* Waveform (when playing) - Hide on very small screens */}
      {isPlaying && audioData && (
        <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
          <AudioWaveform 
            isPlaying={isPlaying} 
            size="small" 
            color={config?.ui_theme?.play_button_icon || "#1976d2"} 
          />
        </Box>
      )}

      {/* Progress Bar - Responsive sizing */}
      {audioData && duration > 0 && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 0.5, sm: 1 }, 
          minWidth: { xs: 60, sm: 120 },
          maxWidth: { xs: '150px', sm: 'none' }, // Limit width on mobile
          flex: '1 1 auto',
          overflow: 'hidden' // Prevent overflow
        }}>
          <Box
            sx={{
              height: { xs: 3, sm: 4 }, // Thinner on mobile
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 2,
              position: 'relative',
              cursor: 'pointer',
              minWidth: { xs: 40, sm: 80 },
              flexGrow: 1
            }}
            onClick={handleProgressClick}
          >            <Box
              sx={{
                height: '100%',
                backgroundColor: config?.ui_theme?.play_button_icon || 'primary.main',
                borderRadius: 2,
                width: `${duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0}%`,
                transition: 'width 0.1s ease'
              }}
            />
          </Box>

          {/* Time Display - Responsive sizing and hide on very small screens */}
          <Typography variant="caption" sx={{ 
            minWidth: { xs: 35, sm: 55 }, 
            fontSize: { xs: '0.6rem', sm: '0.7rem' },
            color: 'text.secondary',
            display: { xs: 'none', sm: 'block' }, // Hide time on mobile when space is tight
            whiteSpace: 'nowrap' // Prevent text wrapping
          }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </Typography>
        </Box>
      )}

      {/* Error Display - Mobile optimized */}
      {error && (
        <Typography variant="caption" sx={{ 
          color: 'error.main', 
          fontSize: { xs: '0.6rem', sm: '0.65rem' },
          whiteSpace: 'nowrap'
        }}>
          Error
        </Typography>
      )}

      {/* Generating Indicator - Mobile optimized */}
      {isGenerating && (
        <Typography variant="caption" sx={{ 
          color: 'text.secondary', 
          fontSize: { xs: '0.6rem', sm: '0.7rem' },
          fontStyle: 'italic',
          whiteSpace: 'nowrap'
        }}>
          Generating...
        </Typography>
      )}
    </Box>
  );
};

const MessageList: React.FC = () => {
  const { messages, isProcessing, sendTTSRequest, sendMessage, sendCopyClick, sendCitationClick, sendProductAction, sendProductActionTracking } = useConversation();
  const { config } = useAppConfig();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Debug: Log hover configuration when config changes
  useEffect(() => {
    if (config?.ui_theme) {
      console.log('Sample question hover config:', {
        hover_background: config.ui_theme.sample_question_hover_background,
        hover_text: config.ui_theme.sample_question_hover_text,
        hover_border: config.ui_theme.sample_question_hover_border
      });
    }
  }, [config]);
  
  // State to track citations for each message
  const [messageCitations, setMessageCitations] = useState<Record<string, Citation[]>>({});

  // Quick View Modal state (shared across all messages)
  const [quickViewProduct, setQuickViewProduct] = useState<ImageMetadata | null>(null);
  const [quickViewMessageId, setQuickViewMessageId] = useState<string | null>(null);

  // Quick View Modal handlers
  const handleQuickViewOpen = useCallback((product: ImageMetadata, messageId: string) => {
    // Don't open during streaming
    const streamingMessage = messages.find(msg => msg.isStreaming);
    if (streamingMessage) return;

    setQuickViewProduct(product);
    setQuickViewMessageId(messageId);

    // Track quickview_open event
    sendProductActionTracking(
      'quickview_open',
      'quickview',
      product.product_url || '',
      product.product_title || product.alt_text,
      messageId
    );
  }, [messages, sendProductActionTracking]);

  const handleQuickViewClose = useCallback(() => {
    // Track quickview_close event
    if (quickViewProduct) {
      sendProductActionTracking(
        'quickview_close',
        'quickview',
        quickViewProduct.product_url || '',
        quickViewProduct.product_title || quickViewProduct.alt_text,
        quickViewMessageId || undefined
      );
    }

    setQuickViewProduct(null);
    setQuickViewMessageId(null);
  }, [quickViewProduct, quickViewMessageId, sendProductActionTracking]);

  const handleQuickViewAddToCart = useCallback((product: ImageMetadata) => {
    // Track add_to_cart event
    sendProductActionTracking(
      'add_to_cart',
      'quickview',
      product.product_url || '',
      product.product_title || product.alt_text,
      quickViewMessageId || undefined,
      product.price || undefined,
      product.metadata?.price != null ? Number(product.metadata.price) : undefined
    );
  }, [quickViewMessageId, sendProductActionTracking]);

  // Memoized function to handle citations extracted from FormattedText
  const handleCitationsExtracted = useCallback((messageId: string, citations: Citation[]) => {
    setMessageCitations(prev => {
      // Only update if citations actually changed to prevent unnecessary re-renders
      const existingCitations = prev[messageId];
      if (existingCitations && JSON.stringify(existingCitations) === JSON.stringify(citations)) {
        return prev;
      }
      return {
        ...prev,
        [messageId]: citations
      };
    });
  }, []);  // Handle copy message content
  const handleCopyMessage = useCallback(async (content: string, messageId: string) => {
    try {
      // Track copy click event
      if (sendCopyClick) {
        sendCopyClick(content, messageId);
      }
      
      // Remove citation JSON patterns first
      let cleanContent = content
        // Remove citation arrays like [{'url': '...', 'title': '...', 'content': '...'}]
        .replace(/\[\s*\{[^}]*'url'[^}]*\}[^\]]*\]/g, '')
        // Remove individual citation objects like {'url': '...', 'title': '...', 'content': '...'}
        .replace(/\{[^}]*'url'[^}]*\}/g, '')
        // Remove citation arrays with double quotes like [{"url": "...", "title": "...", "content": "..."}]
        .replace(/\[\s*\{[^}]*"url"[^}]*\}[^\]]*\]/g, '')
        // Remove individual citation objects with double quotes like {"url": "...", "title": "...", "content": "..."}
        .replace(/\{[^}]*"url"[^}]*\}/g, '')
        // Clean up any remaining empty brackets
        .replace(/\[\s*\]/g, '')
        // Remove extra whitespace and newlines left by citation removal
        .replace(/\n\s*\n\s*\n/g, '\n\n')
        .trim();

      // Create a temporary element to render the formatted text and extract plain text
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cleanContent
        .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold markdown
        .replace(/\*(.*?)\*/g, '$1')     // Remove italic markdown
        .replace(/`(.*?)`/g, '$1')       // Remove code markdown
        .replace(/#{1,6}\s*(.*?)$/gm, '$1') // Remove headers
        .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links, keep text
        .replace(/<br\s*\/?>/gi, '\n')   // Convert line breaks
        .replace(/<[^>]*>/g, '')         // Remove any remaining HTML tags
        .trim();
      
      const textToCopy = tempDiv.textContent || tempDiv.innerText || cleanContent;
      
      // Try modern Clipboard API first (requires secure context and permissions)
      if (navigator.clipboard && window.isSecureContext) {
        try {
          await navigator.clipboard.writeText(textToCopy);
          console.log('Message copied to clipboard using Clipboard API');
          return;
        } catch (clipboardError) {
          console.warn('Clipboard API failed, falling back to legacy method:', clipboardError);
        }
      }
      
      // Fallback method for browsers without Clipboard API or permission issues
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.style.opacity = '0';
      textArea.setAttribute('readonly', '');
      textArea.setAttribute('aria-hidden', 'true');
      
      document.body.appendChild(textArea);
      
      // Select the text
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, textArea.value.length);
      
      // Attempt to copy using the legacy execCommand
      const successful = document.execCommand('copy');
      
      // Clean up
      document.body.removeChild(textArea);
      
      if (successful) {
        console.log('Message copied to clipboard using legacy method');
      } else {
        throw new Error('Both Clipboard API and execCommand failed');
      }        } catch (error) {
      console.error('Failed to copy message:', error);
      
      // Show user-friendly error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Copy failed: ${errorMessage}\n\nTry:\n1. Use Ctrl+C to copy selected text manually\n2. Ensure you're on HTTPS or localhost\n3. Check browser permissions for clipboard access`);
    }
  }, [sendCopyClick]);
  // Handle sample question click
  const handleSampleQuestionClick = useCallback((question: string) => {
    sendMessage(question);
  }, [sendMessage]);

  // Handle product tile category click
  const handleCategoryClick = useCallback((category: string) => {
    sendMessage(category);
  }, [sendMessage]);  // Use refs to track scroll triggers without causing re-renders
  const scrollTriggerRef = useRef({ messagesLength: 0, streamingContent: '' });

  // Simple effect that only runs when messages change
  useEffect(() => {
    const autoscrollEnabled = config?.ui_behavior?.enable_autoscroll !== false;
    
    const currentLength = messages.length;
    const streamingMessage = messages.find(msg => msg.isStreaming);
    const currentStreamingContent = streamingMessage?.content || '';
    
    const prevTrigger = scrollTriggerRef.current;
    let shouldScroll = false;
    
    // Check if a new message was added
    const newMessageAdded = currentLength > prevTrigger.messagesLength;

    // ALWAYS scroll when new messages are added (user or assistant start)
    if (newMessageAdded) {
      shouldScroll = true;
    }
    // Only scroll for streaming updates if autoscroll is enabled
    else if (autoscrollEnabled && streamingMessage && currentStreamingContent && 
             currentStreamingContent !== prevTrigger.streamingContent) {
      shouldScroll = true;
    }

    if (shouldScroll) {
      // Debounce scrolling
      const timeoutId = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 50);

      // Update tracking refs
      scrollTriggerRef.current = {
        messagesLength: currentLength,
        streamingContent: currentStreamingContent
      };

      return () => clearTimeout(timeoutId);
    } else {
      // Update refs even if we don't scroll
      scrollTriggerRef.current = {
        messagesLength: currentLength,
        streamingContent: currentStreamingContent
      };
    }
  }, [messages, config?.ui_behavior?.enable_autoscroll]);
  if (messages.length === 0) {
    // Determine what to display based on welcome_display_mode configuration
    const displayMode = config?.ui_text?.welcome_display_mode || 'both';
    const showTiles = displayMode === 'both' || displayMode === 'tiles_only';
    const showQuestions = displayMode === 'both' || displayMode === 'questions_only';

    return (
      <Box sx={{
        p: 4,
        textAlign: 'center',
        color: 'text.secondary'
      }}>        <Typography 
          variant="body1" 
          sx={{ 
            mb: 3,
            fontWeight: config?.ui_text?.welcome_message_font_weight || 'normal',
            fontStyle: config?.ui_text?.welcome_message_font_style || 'normal',
            fontSize: config?.ui_text?.welcome_message_font_size || '1rem',
            color: config?.ui_text?.welcome_message_color || 'inherit',
            textAlign: config?.ui_text?.welcome_message_text_align || 'center'
          }}
        >
          {config?.ui_text?.welcome_message || "Start a conversation by speaking or typing a message"}
        </Typography>

        {/* Product Tiles - Masonry Grid */}
        {showTiles && config?.product_tiles?.enabled && (
          <ProductTiles onCategoryClick={handleCategoryClick} />
        )}

          {/* Sample Questions */}
        {showQuestions && config?.sample_questions && config.sample_questions.length > 0 && (
          <Box sx={{ mt: 2 }}>            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
              {config?.ui_text?.sample_questions_label || ""}
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              justifyContent: 'center',
              maxWidth: '600px',
              margin: '0 auto'
            }}>              {config.sample_questions.map((question, index) => (                <Chip
                  key={index}
                  label={question}
                  variant="outlined"
                  size="small"
                  clickable
                  onClick={() => handleSampleQuestionClick(question)}                  sx={{
                    backgroundColor: config.ui_theme?.sample_question_background || '#e3f2fd',
                    borderColor: config.ui_theme?.sample_question_border || '#1976d2',
                    color: config.ui_theme?.sample_question_text || '#1976d2',
                    '&:hover': {
                      backgroundColor: `${config.ui_theme?.sample_question_hover_background || '#1976d2'} !important`,
                      color: `${config.ui_theme?.sample_question_hover_text || '#ffffff'} !important`,
                      borderColor: `${config.ui_theme?.sample_question_hover_border || '#1976d2'} !important`,
                    },
                    '&.MuiChip-clickable:hover': {
                      backgroundColor: `${config.ui_theme?.sample_question_hover_background || '#1976d2'} !important`,
                      color: `${config.ui_theme?.sample_question_hover_text || '#ffffff'} !important`,
                      borderColor: `${config.ui_theme?.sample_question_hover_border || '#1976d2'} !important`,
                    },
                    '&.MuiChip-outlined:hover': {
                      backgroundColor: `${config.ui_theme?.sample_question_hover_background || '#1976d2'} !important`,
                      color: `${config.ui_theme?.sample_question_hover_text || '#ffffff'} !important`,
                      borderColor: `${config.ui_theme?.sample_question_hover_border || '#1976d2'} !important`,
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  }  return (
    <Box sx={{
      p: 2,
      height: '100%',
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: 2
    }}>
      {messages.map((message, index) => {
        const citations = messageCitations[message.id] || [];

        return (          <MessageItem
            key={message.id}
            message={message}
            index={index}
            citations={citations}
            onCitationsExtracted={handleCitationsExtracted}
            onCopyMessage={handleCopyMessage}
            onTTSRequest={sendTTSRequest}
            onCitationClick={sendCitationClick}
            onProductAction={sendProductAction}
            onQuickViewOpen={handleQuickViewOpen}
            config={config}
          />
        );
      })}

      {/* Processing indicator - only show if not streaming and processing */}
      {isProcessing && !messages.some(msg => msg.isStreaming) && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          py: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {/* AI Avatar */}
            <Box sx={{
              backgroundColor: '#f5f5f5',
              borderRadius: '50%',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',              flexShrink: 0
            }}>
              <Assistant sx={{ fontSize: 20, color: '#666' }} />
            </Box>

            {/* Typing indicator */}
            <TypingIndicator isVisible={isProcessing} />
          </Box>
        </Box>
      )}

      <div ref={messagesEndRef} />

      {/* Product Quick View Modal */}
      <ProductQuickViewModal
        open={!!quickViewProduct}
        onClose={handleQuickViewClose}
        product={quickViewProduct}
        onAddToCart={handleQuickViewAddToCart}
        messageId={quickViewMessageId || undefined}
      />
    </Box>
  );
};

// Memoized individual message component to prevent unnecessary re-renders
const MessageItem = memo(({ 
  message, 
  index, 
  citations, 
  onCitationsExtracted, 
  onCopyMessage, 
  onTTSRequest,
  onCitationClick,
  onProductAction,
  onQuickViewOpen,
  config
}: {
  message: any;
  index: number;
  citations: Citation[];
  onCitationsExtracted: (messageId: string, citations: Citation[]) => void;
  onCopyMessage: (content: string, messageId: string) => void;
  onTTSRequest: (content: string, messageId: string) => void;
  onCitationClick: (citationUrl: string, citationTitle: string, citationIndex?: number, messageId?: string) => void;
  onProductAction: (actionType: ProductActionType, sourceProduct: ProductActionSourceProduct) => void;
  onQuickViewOpen: (product: ImageMetadata, messageId: string) => void;
  config: any;
}) => {
  const messageFlexDirection = getMessageFlexDirection(message.content, message.type === 'user');
  const messageId = message.id || `${index}`;
  const [copySuccess, setCopySuccess] = useState(false);

  // Create a stable callback for this specific message
  const handleCitationsForMessage = useCallback((citations: Citation[]) => {
    onCitationsExtracted(messageId, citations);
  }, [messageId, onCitationsExtracted]);
  // Handle copy with success indicator
  const handleCopyClick = useCallback(async () => {
    await onCopyMessage(message.content, messageId);
    setCopySuccess(true);
  }, [onCopyMessage, message.content, messageId]);

  // Reset copy success when mouse leaves the button
  const handleMouseLeave = useCallback(() => {
    setCopySuccess(false);
  }, []);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: messageFlexDirection,
        alignItems: 'flex-start',
        gap: 1
      }}
    >      {/* Avatar */}
      <Box sx={{
        backgroundColor: message.type === 'user' 
          ? (config?.ui_theme?.user_icon_background || '#e3f2fd') 
          : (config?.ui_theme?.assistant_icon_background || '#f5f5f5'),
        borderRadius: '50%',
        width: 40,
        height: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        {message.type === 'user' ? (
          <Person sx={{ fontSize: 20, color: config?.ui_theme?.user_icon_color || '#1976d2' }} />
        ) : (
          <Assistant sx={{ fontSize: 20, color: config?.ui_theme?.assistant_icon_color || '#666' }} />
        )}
      </Box>      {/* Message Content */}
      <Paper
        elevation={1}
        sx={{
          p: {
            xs: 1.5, // Mobile: Reduced padding for more content space
            sm: 1.75, // Tablet: Moderate padding
            md: 2     // Desktop: Keep current padding
          },
          // Width: Assistant messages expand for grid layout, user messages fit content
          ...(message.type === 'assistant' && {
            width: {
              xs: '95%',
              sm: '85%',
              md: '80%'
            }
          }),
          maxWidth: {
            xs: message.type === 'assistant' ? '95%' : '90%', // Mobile: Much wider for better list display
            sm: message.type === 'assistant' ? '85%' : '75%', // Tablet: Moderate increase
            md: '80%' // Desktop: Wider for better content display
          },
          backgroundColor: message.type === 'user' 
            ? (config?.ui_theme?.user_message_background || '#1976d2') 
            : (config?.ui_theme?.assistant_message_background || '#fff'),
          color: message.type === 'user' 
            ? (config?.ui_theme?.user_message_text || 'white') 
            : (config?.ui_theme?.assistant_message_text || 'text.primary'),
          borderRadius: 2,
          border: message.type === 'user' 
            ? `1px solid ${config?.ui_theme?.user_message_border || '#1976d2'}` 
            : `1px solid ${config?.ui_theme?.assistant_message_border || '#e0e0e0'}`,
          position: 'relative', // Ensure proper positioning context
          overflow: 'visible', // Allow action buttons to be fully visible
          ...(message.type === 'user' ? {
            borderBottomRightRadius: 4
          } : {
            borderBottomLeftRadius: 4
          })
        }}
      >
        {/* Text content - Displayed above Image Carousel */}
        {message.type === 'assistant' && message.isStreaming ? (
          <FormattedText
            content={message.content}
            variant="body1"
            isStreaming={true}
            isUserMessage={false}
            sx={getTextDirectionStyles(message.content)}
            onCitationsExtracted={handleCitationsForMessage}
          />
        ) : message.type === 'user' && message.messageType === 'audio' && message.isStreaming ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress
              size={16}
              thickness={4}
              sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
            />
            <FormattedText
              content={message.content}
              variant="body1"
              isUserMessage={true}
              sx={{
                fontStyle: 'italic',
                opacity: 0.8,
                ...getTextDirectionStyles(message.content)
              }}
            />
            <AudioWaveform isPlaying={true} size="small" color="rgba(255, 255, 255, 0.7)" />
          </Box>
        ) : message.type === 'user' && message.messageType === 'image_search' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ImageThumbnailChip
              imageData={message.imageData}
              caption={message.imageCaption}
              keywords={undefined} // Don't show keywords in the chip
              isProcessing={message.isStreaming}
              textColor={config?.ui_theme?.user_message_text || 'white'}
            />
            {/* Only show keywords in debug mode */}
            {config.debug && !message.isStreaming && message.imageKeywords && (
              <Typography
                variant="caption"
                sx={{
                  color: config?.ui_theme?.user_message_text || 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  fontStyle: 'italic',
                }}
              >
                🔍 Debug - Keywords: {message.imageKeywords}
              </Typography>
            )}
          </Box>
        ) : (
          <FormattedText
            content={message.content}
            variant="body1"
            isUserMessage={message.type === 'user'}
            sx={getTextDirectionStyles(message.content)}
            onCitationsExtracted={handleCitationsForMessage}
          />
        )}

        {/* Image Carousel - Displayed below text content */}
        {message.type === 'assistant' && message.images && message.images.length > 0 && (
          <ImageCarousel 
            images={message.images}
            citations={citations}
            messageContent={message.content} // NEW: Pass message content for structured response parsing
            onImageClick={(image: ImageMetadata) => {
              // Check if QuickView is enabled (default: true)
              const quickViewEnabled = config?.ui_quickview?.enabled !== false;
              
              if (quickViewEnabled) {
                // Open QuickView modal
                onQuickViewOpen(image, message.id);
              } else {
                // Fallback: Track click and open in new tab
                onCitationClick(
                  image.product_url,
                  image.alt_text || image.title || 'Product Image',
                  undefined,
                  message.id
                );
                window.open(image.product_url, '_blank', 'noopener,noreferrer');
              }
            }}
            onOutfitSuggestion={(sourceProduct: ProductActionSourceProduct) => {
              // Send product action request for outfit suggestions
              onProductAction('outfit_suggestion', sourceProduct);
            }}
          />
        )}

        {/* AI thinking indicator - Moved above image carousel */}
        {message.isStreaming && message.type === 'assistant' && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              sx={{
                opacity: 0.7,
                backgroundColor: 'rgba(255,255,255,0.2)',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.7rem'
              }}
            >
              💭 AI is thinking...
            </Typography>
          </Box>
        )}

        {/* Action Buttons - Responsive layout for mobile optimization */}
        {message.type === 'assistant' && !message.isStreaming && (          <Box sx={{ 
            mt: 2, 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'flex-start', sm: 'space-between' }, 
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 1.5, sm: 2 },
            overflow: 'visible',
            width: '100%'
          }}>
            {/* Left side - Citations and Audio Player */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center',
              minWidth: 0,
              flex: { xs: 'none', sm: '0 0 auto' },
              overflow: 'visible'
            }}>
              {/* Sources Button */}
              {config?.ui_behavior?.enable_citations_button !== false && citations.length > 0 && (
                <CitationFlyout citations={citations} messageId={messageId} />
              )}
              
              {/* Inline Audio Player - conditionally rendered based on config */}
              {config?.ui_enable_audio_controls !== false && (
                <InlineAudioPlayer
                  messageContent={message.content}
                  messageId={messageId}
                  isGenerating={message.isGeneratingTTS || false}
                  audioData={message.audioData}
                  onTTSRequest={onTTSRequest}
                />
              )}
            </Box>

            {/* Right side - Feedback buttons and Copy button */}
            <Box sx={{ 
              display: 'flex', 
              gap: 0.5, 
              alignItems: 'center',
              flexShrink: 0,
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              {/* Feedback Buttons (Thumbs up, Thumbs down) */}
              <FeedbackButtons messageId={messageId} />
              
              {/* Copy button - conditionally rendered based on config */}
              {config?.ui_enable_copy_button !== false && (
                <Tooltip title={copySuccess ? (config?.ui_tooltips?.copied_tooltip || "Copied!") : (config?.ui_tooltips?.copy_text_tooltip || "Copy Text")}>
                  <IconButton
                    size="small"
                    onClick={handleCopyClick}
                    onMouseLeave={handleMouseLeave}
                    sx={{
                      color: copySuccess ? 'success.main' : 'text.secondary',
                      '&:hover': {
                        color: copySuccess ? 'success.dark' : 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {copySuccess ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>)}

        {/* Timestamp and message type icon row */}
        {(config?.ui_behavior?.show_message_timestamps !== false || (config?.ui_behavior?.show_message_type !== false && message.messageType && !message.isStreaming)) && (
          <Box sx={{ 
            mt: 1, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center' 
          }}>
            {/* Timestamp */}
            {config?.ui_behavior?.show_message_timestamps !== false && (
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.7
                }}
              >
                {new Date(message.timestamp).toLocaleTimeString()}
              </Typography>
            )}
          
            {/* Message type icon - right justified */}
            {(config?.ui_behavior?.show_message_type !== false && message.messageType && !message.isStreaming) && (            <Tooltip title={
              message.messageType === 'audio' && message.type === 'user' ? 'Voice Message' :
              message.messageType === 'audio' && message.type === 'assistant' ? 'Voice Message' :
              message.messageType === 'tts' ? 'Audio Message' :
              message.messageType === 'image_search' ? 'Image Search' :
              message.messageType === 'product_action' ? 'Product Action' :
              message.messageType === 'text' && message.type === 'user' ? 'Text Message' : ''
            }>
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.7,
                  backgroundColor: message.type === 'user' 
                    ? (config?.ui_theme?.user_message_type_background || 'rgba(255,255,255,0.2)')
                    : (config?.ui_theme?.assistant_message_type_background || 'rgba(0,0,0,0.1)'),
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.7rem',
                  cursor: 'help'
                }}
              >
                {message.messageType === 'audio' && message.type === 'user' && '🎤'}
                {message.messageType === 'audio' && message.type === 'assistant' && '🎤'}
                {message.messageType === 'tts' && '🔊'}
                {message.messageType === 'image_search' && '🖼️'}
                {message.messageType === 'product_action' && '🛍️'}
                {message.messageType === 'text' && message.type === 'user' && '💬'}
              </Typography>
            </Tooltip>
          )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memoization
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    prevProps.message.isGeneratingTTS === nextProps.message.isGeneratingTTS &&
    prevProps.message.audioData === nextProps.message.audioData &&
    JSON.stringify(prevProps.citations) === JSON.stringify(nextProps.citations)
  );
});

export default MessageList;